import { ref } from 'vue'
import { useUserStore } from '@/store/pinia/UserStore'
import type { PlanTier } from '@/composables/useTieredPlans'
import { useTieredPlan } from '@/composables/useTieredPlans'
import { useStripe } from '@/composables/useStripe'
import { trackConversion } from '@/services/AnalyticsService'
import api from '@/services/Api'

export interface PromotionDiscount {
  percent_off?: number
  amount_off?: number
}

export function useSubscription() {
  const processing = ref(false)
  const awaitingSubscriptionResponse = ref(false)
  const paymentSuccessful = ref(false)
  const error = ref<string | null>(null)

  const userStore = useUserStore()
  const { clearSelectedPlan } = useTieredPlan()
  const { elements, payByCard } = useStripe()

  const submitSubscriptionRequest = async (
    paymentMethod: string | undefined,
    selectedTier: PlanTier | null,
    promoCode?: string | null,
    onSuccess?: () => void,
    discount?: PromotionDiscount | null,
  ) => {
    if (awaitingSubscriptionResponse.value) {
      return
    }
    try {
      awaitingSubscriptionResponse.value = true
      const res = await api().Post('/v3/subscribe', {
        payment_method: paymentMethod,
        plan_id: selectedTier?.product.id,
        price_id: selectedTier?.price.id,
        promo_code: promoCode,
      })

      if (res.status === 200 || res.status === 201) {
        await userStore.fetchUser()
        paymentSuccessful.value = true
        clearSelectedPlan()
        onSuccess?.()
        const originalPrice = (selectedTier?.price.amount ?? 0) / 100
        let finalPrice = originalPrice

        if (discount?.percent_off) {
          finalPrice = originalPrice * (1 - discount.percent_off / 100)
        } else if (discount?.amount_off) {
          finalPrice = Math.max(0, originalPrice - discount.amount_off / 100)
        }

        const additionalData: Record<string, string | number | null> = {}
        if (discount && promoCode) {
          additionalData.coupon_code = promoCode
          additionalData.original_price = originalPrice
          additionalData.discount_percent = discount.percent_off || null
          additionalData.discount_amount = discount.amount_off || null
          additionalData.final_price = finalPrice
        }

        trackConversion(`${selectedTier?.value_tier}-subscription`, finalPrice, additionalData)
      } else {
        throw new Error('Failed to complete payment')
      }
    } catch (err) {
      processing.value = false
      awaitingSubscriptionResponse.value = false
      console.error('Failed to complete payment', err)
      error.value = 'Failed to complete payment. Please try again.'
    }
  }

  const isFreeWithCoupon = (
    selectedTier: PlanTier | null,
    discount: PromotionDiscount | null = null,
  ) => {
    if (!selectedTier || !discount) return false

    const originalPrice = selectedTier.price.amount

    if (discount.percent_off && discount.percent_off >= 100) {
      return true
    }

    if (discount.amount_off && discount.amount_off >= originalPrice) {
      return true
    }

    return false
  }

  const handlePaymentAttempt = async (
    selectedTier: PlanTier | null,
    promoCode?: string,
    onSuccess?: () => void,
    promotionCodeDiscount?: PromotionDiscount | null,
  ) => {
    processing.value = true
    error.value = null

    // If coupon makes it free, skip payment method creation entirely
    if (isFreeWithCoupon(selectedTier, promotionCodeDiscount)) {
      await submitSubscriptionRequest(
        undefined,
        selectedTier,
        promoCode,
        onSuccess,
        promotionCodeDiscount,
      )
      return
    }

    elements.value?.submit()
    payByCard()
      .then((result) => {
        if (!result || result.error) {
          error.value = result?.error.message || 'Error creating payment method'
          processing.value = false
          return
        }

        if (!result.paymentMethod) {
          error.value = 'Error creating payment method'
          processing.value = false
          return
        }

        submitSubscriptionRequest(
          result.paymentMethod.id,
          selectedTier,
          promoCode,
          onSuccess,
          promotionCodeDiscount,
        )
      })
      .catch((err) => {
        error.value = err || 'Error creating payment method'
        console.error(err)
        processing.value = false
      })
  }

  return {
    processing,
    paymentSuccessful,
    error,
    handlePaymentAttempt,
    submitSubscriptionRequest,
  }
}
